import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang, loc } from "../i18n/LanguageContext";
import { api, fileUrl } from "../lib/api";
import { useContent } from "../lib/useContent";
import { useSeo, Icon, Reveal, LazyImg } from "../components/common";
import { HeroButtons, CTABand } from "../components/blocks";
import { ServiceTextCard } from "../components/cards";
import { CATEGORY_NAMES } from "../data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { data: allServices } = useContent("services");

  useEffect(() => {
    setSvc(null); setNotFound(false);
    api.get(`/content/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo(svc ? `${loc(svc, "name", lang)} | Sparko` : "Service | Sparko",
    svc ? loc(svc, "desc", lang) : "Premium cleaning service by Sparko.");

  if (notFound) return (
    <div className="min-h-[60vh] grid place-items-center pt-32 px-5 text-center">
      <div>
        <h1 className="font-display text-3xl text-navy">Service not found</h1>
        <button onClick={() => nav("/services")} className="mt-6 rounded-full bg-blue px-6 py-3 text-white font-semibold">Back to Services</button>
      </div>
    </div>
  );
  if (!svc) return <div className="min-h-[60vh] grid place-items-center"><Icon name="Loader2" size={32} className="animate-spin text-blue" /></div>;

  const name = loc(svc, "name", lang);
  const desc = loc(svc, "desc", lang);
  // Glass / window / facade services are excluded from Related Services per client request
  const EXCLUDED_RELATED = new Set(["window-cleaning", "glass-cleaning", "facade-cleaning"]);
  const related = allServices.filter((s) => s.category === svc.category && s.slug !== svc.slug && !EXCLUDED_RELATED.has(s.slug)).slice(0, 4);
  const catName = CATEGORY_NAMES[svc.category]?.[lang] || svc.category_name;
  const benefits = t.service.benefits;

  return (
    <div>
      <section className="relative overflow-hidden bg-navy grain pt-36 md:pt-44 pb-16">
        {svc.image && (
          <div className="absolute inset-0">
            <img src={fileUrl(svc.image)} alt={name} className="h-full w-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-navy/85 to-navy" />
          </div>
        )}
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/50">
            <Link to="/" className="hover:text-white">{t.nav.home}</Link><Icon name="ChevronRight" size={14} className="rtl:rotate-180" />
            <Link to="/services" className="hover:text-white">{t.nav.services}</Link><Icon name="ChevronRight" size={14} className="rtl:rotate-180" />
            <span className="text-white/80">{name}</span>
          </nav>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full glass-dark px-4 py-2 text-xs font-medium text-white/90">
            <Icon name={svc.icon || "Sparkles"} size={15} className="text-blue" /> {catName}
          </span>
          <h1 className="mt-5 font-display text-4xl md:text-6xl text-white leading-[1.05] max-w-3xl">{name}</h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Reveal><p className="text-xl text-navy font-display leading-snug">{desc}</p></Reveal>
          {svc.image && (
            <Reveal delay={0.15}>
              <LazyImg src={fileUrl(svc.image)} alt={name} className="mt-10 rounded-3xl w-full aspect-[16/9] object-cover shadow-xl" />
            </Reveal>
          )}
        </div>
        <div className="md:col-span-5">
          <div className="sticky top-28 rounded-3xl border border-black/5 bg-white p-8 shadow-lg">
            <h3 className="font-display text-xl text-navy">{t.service.included}</h3>
            <ul className="mt-5 space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-600">
                  <Icon name="CheckCircle2" size={18} className="text-blue mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl bg-navy p-6 text-center">
              <p className="text-white font-display text-lg">{t.service.ready}</p>
              <Link to="/quote" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue px-6 py-3.5 font-semibold text-white hover:bg-[#0052FF]">
                {t.cta.quoteFree} <Icon name="ArrowRight" size={16} className="rtl:rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-muted py-20" style={{ background: "var(--muted)" }}>
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <h2 className="font-display text-3xl text-navy">{t.service.related}</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {related.map((s, i) => <ServiceTextCard key={s.id} svc={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
